import React from 'react'
import { cn } from '@global/utils/tailwind'
import { createPortal } from 'react-dom'
import { useRef, useState, useCallback, useMemo, useLayoutEffect } from 'react'

type Placement = 'top' | 'bottom' | 'left' | 'right'
type Mode = 'dark' | 'light'

interface TooltipProps {
  title: React.ReactNode
  description?: React.ReactNode
  children: React.ReactNode
  placement?: Placement
  arrow?: boolean
  disabled?: boolean
  className?: string
  mode?: Mode
  delay?: number
  modifyPosition?: ModifyPositionFunc
}

export type ModifyPositionFunc = (
  position: Position,
  triggerElement: HTMLDivElement | null,
  triggerElementInner: HTMLDivElement | null,
) => Position

interface Position {
  top: number
  left: number
}

interface TooltipSize {
  width: number
  height: number
}

interface TooltipState {
  isVisible: boolean
  position: Position
  size: TooltipSize
}

const ARROW_SIZE = 8
const Z_INDEX = 99999

const calculateTooltipPosition = (
  triggerRect: DOMRect,
  placement: Placement,
  tooltipSize: TooltipSize,
): Position => {
  const { width, height } = tooltipSize

  switch (placement) {
    case 'top':
      return {
        top: triggerRect.top - height - ARROW_SIZE,
        left: triggerRect.left + triggerRect.width / 2 - width / 2,
      }
    case 'bottom':
      return {
        top: triggerRect.bottom + ARROW_SIZE,
        left: triggerRect.left + triggerRect.width / 2 - width / 2,
      }
    case 'left':
      return {
        top: triggerRect.top + triggerRect.height / 2 - height / 2,
        left: triggerRect.left - width - ARROW_SIZE,
      }
    case 'right':
      return {
        top: triggerRect.top + triggerRect.height / 2 - height / 2,
        left: triggerRect.right + ARROW_SIZE,
      }
  }
}

const getArrowStyles = (placement: Placement, mode: Mode) => {
  const baseClasses = 'absolute w-2 h-2 transform rotate-45'
  const colorClasses = mode === 'light' ? 'bg-white text-slate-800' : 'bg-[#30505A] text-white'

  const placementClasses = {
    top: 'shadow-[2px_2px_4px_rgba(0,0,0,0.1)] top-full left-1/2 transform -translate-x-1/2 -mt-1',
    bottom:
      'shadow-[-2px_-2px_4px_rgba(0,0,0,0.1)] bottom-full left-1/2 transform -translate-x-1/2 mt-1',
    left: 'shadow-[-2px_2px_4px_rgba(0,0,0,0.1)] right-full top-1/2 transform -translate-y-1/2 -mr-1',
    right:
      'shadow-[2px_-2px_4px_rgba(0,0,0,0.1)] left-full top-1/2 transform -translate-y-1/2 -ml-1',
  }

  return cn(baseClasses, placementClasses[placement], colorClasses)
}

const TooltipContent = React.memo<{
  title: React.ReactNode
  description?: React.ReactNode
  placement: Placement
  arrow: boolean
  mode: Mode
  className?: string
  position: Position
  onSizeChange: (size: TooltipSize) => void
}>(({ title, description, placement, arrow, mode, className, position, onSizeChange }) => {
  const tooltipRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect()
      onSizeChange({ width: rect.width, height: rect.height })
    }
  }, [title, description, onSizeChange])

  const containerStyles = useMemo(
    () => ({
      position: 'fixed' as const,
      top: position.top,
      left: position.left,
      zIndex: Z_INDEX,
    }),
    [position],
  )

  const containerClasses = useMemo(
    () =>
      cn(
        'text-[10px] px-3 py-2 rounded shadow max-w-[200px]',
        {
          'bg-white text-slate-800': mode === 'light',
          'bg-[#30505A] text-white': mode === 'dark',
        },
        className,
      ),
    [mode, className],
  )

  const arrowClasses = useMemo(() => getArrowStyles(placement, mode), [placement, mode])

  return (
    <div ref={tooltipRef} style={containerStyles} className={containerClasses}>
      {title}
      {description}
      {arrow && <div className={arrowClasses} />}
    </div>
  )
})

TooltipContent.displayName = 'TooltipContent'

export const Tooltip = ({
  title,
  children,
  placement = 'top',
  arrow = true,
  disabled = false,
  description,
  className,
  mode = 'light',
  delay = 0,
  modifyPosition,
}: TooltipProps) => {
  const [state, setState] = useState<TooltipState>({
    isVisible: false,
    position: { top: 0, left: 0 },
    size: { width: 0, height: 0 },
  })

  const triggerRef = useRef<HTMLDivElement>(null)
  const triggerInnerRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout>()

  const updatePosition = useCallback(() => {
    if (triggerRef.current && state.size.width > 0 && state.size.height > 0) {
      const rect = triggerRef.current.getBoundingClientRect()
      const newPosition = calculateTooltipPosition(rect, placement, state.size)
      setState(prev => ({ ...prev, position: newPosition }))
    }
  }, [placement, state.size])

  const handleSizeChange = useCallback((size: TooltipSize) => {
    setState(prev => ({ ...prev, size }))
  }, [])

  const showTooltip = useCallback(() => {
    if (disabled) return

    if (delay > 0) {
      timeoutRef.current = setTimeout(() => {
        setState(prev => ({ ...prev, isVisible: true }))
      }, delay)
    } else {
      setState(prev => ({ ...prev, isVisible: true }))
    }
  }, [disabled, delay])

  const hideTooltip = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setState(prev => ({ ...prev, isVisible: false }))
  }, [])

  useLayoutEffect(() => {
    if (state.isVisible) {
      updatePosition()
    }
  }, [state.isVisible, state.size, updatePosition])

  useLayoutEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const shouldRenderTooltip = useMemo(
    () => typeof document !== 'undefined' && state.isVisible && !disabled,
    [state.isVisible, disabled],
  )
  return (
    <div ref={triggerRef} className="relative flex">
      <div
        ref={triggerInnerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        {children}
        {shouldRenderTooltip &&
          createPortal(
            <TooltipContent
              title={title}
              description={description}
              placement={placement}
              arrow={arrow}
              mode={mode}
              className={className}
              position={
                modifyPosition
                  ? modifyPosition(state.position, triggerRef.current, triggerInnerRef.current)
                  : state.position
              }
              onSizeChange={handleSizeChange}
            />,
            document.body,
          )}
      </div>
    </div>
  )
}
